<template>
  <div class="product-wrapper">
    <div class="product-head-section">
      <b-container>
        <b-row>
          <b-col sm="12">
            <div class="product-head">
              <div class="image-container">
                <div class="product-image-wrapper">
                  <AImage
                    :img="item.imagesGallery[0]"
                    preset="wineproductdetails"
                    responsive
                    contain
                  />
                </div>
              </div>
              <div class="main-container">
                <div class="main-container-content">
                  <div
                    v-if="item.seriesLabel && showSeries"
                    class="series"
                    v-text="item.seriesLabel"
                  />
                  <div class="title titled-product-page">
                    <h1>{{ item.title }}</h1>
                    <div class="product-tags">
                      <div
                        v-if="item.award_winner"
                        class="special-tag"
                      >
                        <div class="tag-content">
                          Award Winner
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="abstract"
                    v-html="item.abstract"
                  />
                  <div
                    v-if="item.product_links && item.product_links.length > 0"
                    class="product-links-element"
                  >
                    <label for="links">{{ linksTitle }}</label>
                    <b-form-select
                      id="links"
                      v-model="currentLink"
                      :options="linkOptions"
                      @change="linkChange"
                    />
                  </div>
                  <ecommerce-cart-add-block
                    :node="item"
                    :overriderrp="item.rrp"
                  />
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </div>
    <div
      v-if="displayTabs"
      class="wine-details-wrapper"
    >
      <b-container>
        <b-row>
          <b-col>
            <b-tabs
              pills
              class="wine-tabs"
            >
              <b-tab
                v-if="displayDetailsTab"
                title="Wine Details"
                active
              >
                <deferred-content-placeholder v-if="!productDetailIsComplete" />
                <div
                  v-else
                  class="wine-details-content"
                >
                  <div
                    v-if="item.description"
                    class="wine-details-element tasting-desc"
                  >
                    <WineProductDescription
                      v-if="item.description"
                      :description="item.description"
                    />
                  </div>
                  <div
                    v-if="hasProductDetails(item)"
                    class="wine-details-element wine-details"
                  >
                    <WineProductDetails
                      :technical-notes="item.technical_notes"
                      :features="item.features"
                      :tasting-notes="item.tasting_notes"
                      :wine-making="item.wine_making"
                      :previous-tasting-notes="item.prev_tasting_notes"
                    />
                  </div>
                </div>
              </b-tab>
              <b-tab
                v-if="!productDetailIsComplete || item.reviews.length > 0"
                title="Reviews & Awards"
              >
                <deferred-content-placeholder v-if="!productDetailIsComplete" />
                <div
                  v-else
                  class="product-reviews-container"
                >
                  <WineProductReviews :reviews="item.reviews" />
                </div>
              </b-tab>
              <b-tab
                v-if="faqs && faqs.length > 0"
                title="FAQ, Shipping & Delivery"
              >
                <div class="shipping-information-wrapper">
                  <b-container>
                    <div class="shipping-information">
                      <div class="shipping-content">
                        <FaqList :faqs="faqs" />
                      </div>
                    </div>
                  </b-container>
                </div>
              </b-tab>
            </b-tabs>
          </b-col>
        </b-row>
      </b-container>
    </div>

    <relatedProducts
      v-if="relatedProducts.length > 0"
      :related-products="relatedProducts"
      :theme="productTheme"
      :hover-card-setting="hoverCardSetting"
    />
  </div>
</template>

<script>
import WineProductDescription from './WineProductDescription'
import WineProductDetails from './WineProductDetails'
import FaqList from 'components/website/content-lists/faq/0001/faq-list'
import WineProductReviews from './WineProductReviews'
import relatedProducts from '../common/related-products'
import DeferredContentPlaceholder from 'components/generic/loaders/deferred-content-placeholder'

// mixins
import filters from '../common/mixins/filters'
import methods from '../common/mixins/methods'
import preparation from '../common/mixins/preparation'
import settings from '../common/mixins/settings'
import productDetailLazyLoad from 'src/app/product-detail-lazy-load-mixin'

export default {
  name: 'wine-product-page',
  components: {
    'WineProductDescription': WineProductDescription,
    'WineProductDetails': WineProductDetails,
    'FaqList': FaqList,
    'deferred-content-placeholder': DeferredContentPlaceholder,
    'WineProductReviews': WineProductReviews,
    'relatedProducts': relatedProducts
  },
  mixins: [ filters, methods, preparation, settings, productDetailLazyLoad ],
  computed: {
    linksTitle() { return this.item.product_links_name ? this.item.product_links_name : 'Options' },
    currentLink: {
      get() {
        if (this.linkOptions.find(({ value }) => value === this.$route.path)) return this.$route.path
        if (this.linkOptions.find(({ value }) => value === this.$route.path.slice(1))) return this.$route.path.slice(1)
        return ''
      },
      set() {}
    },
    linkOptions() {
      const options = []
      for (const { link } of this.item.product_links) {
        if (link.url && link.title) {
          options.push({ value: link.url, text: link.title })
        }
      }
      return options
    },
    showSeries() { return this.$store.getters.getModuleSetting('theme', 'showSeriesLabel', true) },
    // hasProductDetails/reviews read fields the SSR-inlined state can defer
    // (see product-detail-lazy-load-mixin) - while incomplete, assume there
    // might be something to show so the tab appears with a loading state
    // instead of flashing hidden-then-visible once the fetch resolves.
    displayDetailsTab() { return !this.productDetailIsComplete || this.item.description.length > 0 || this.hasProductDetails(this.item) },
    displayTabs() { return !this.productDetailIsComplete || this.item.reviews.length > 0 || (this.faqs && this.faqs.length > 0) || this.displayDetailsTab }
  },
  methods: {
    linkChange(url) {
      if (url.includes('://')) {
        window.location.href = url
        return
      }
      this.$router.push(this.addSlash(url))
    },
    addSlash(url) {
      if (url.startsWith('/')) return url
      return `/${url}`
    }
  }
}
</script>

<style scoped lang="scss">
@import '~@scss/custom.scss';

.wine-tabs {
 ::v-deep .nav-pills {
    display: flex;
    justify-content: center;
    list-style: none;
    outline: none;
    padding: 0em;
    margin-bottom: 1.5em;

    .nav-item {
      align-items: center;
      display: flex;
      padding: 1em .25em;
      font-size: 1em;

      @include media-breakpoint-up(md) {
        font-size: 1.2em;
        padding: 0em .5em 1em .5em;
      }

      .nav-link {
        outline: none;
        text-decoration: none;
        background-color: transparent;
        border-radius: 0px;
        color: var(--main-text);

        &:hover {
          box-shadow: 0px 2px 0px var(--main-highlight-text-color);
          ;
        }

        &.active {
          color: var(--main-highlight-text-color);
          box-shadow: 0px 2px 0px var(--main-highlight-text-color);
        }
      }
    }
  }
}

.tab-content>.active {
  outline: none !important;
}

.product-wrapper {
  ::v-deep .product-image-wrapper {
    display: flex;
    transition: var(--main-transition);
    position: relative;
    .a-image {
      padding-bottom:var(--main-product-image-aspect-ratio, 100%);
      background:var(--main-product-image-background);
      img {
        padding: 1.5em;
      }
    }
  }
}

.product-wrapper {
 ::v-deep .pack-image {
    height: 370px;
    width: 100%;
    margin: 0em auto;
    justify-content: center;
    display: flex;
    align-items: center;
    padding: 1em;

    img {
      max-height: 100%;
      width: auto;
    }
  }

  .product-head-section {
    padding: 3em 0em;
  }

  .product-head {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;

    .image-container {
      position: relative;
      width: 100%;
      margin-bottom: 2rem;

      @include media-breakpoint-up(md) {
        margin: 0;
        width: calc(40% - 1.5rem);
      }

      @include media-breakpoint-up(lg) {
        width: calc(30% - 1.5rem);
      }
    }

    .main-container {
      width: 100%;

      @include media-breakpoint-up(md) {
        width: 60%;
      }

      @include media-breakpoint-up(lg) {
        width: 70%;
      }

      .main-container-content {

        .series {
          font-size: 1.25em;
        }

        .title {
          border-bottom: 1px solid;
          border-color: var(--main-border-color);
          margin-bottom: 1.5em;
          padding-bottom: 1.5em;

          h1 {
            font-size: 2.75em;
            margin: 0em;
          }

          .product-tags {
            position: absolute;
            top: .75em;
            left: .75em;
            z-index: 1;

            .special-tag {
              background: var(--main-award-badge-background-color);
              border-radius: 50%;
              width: 5em;
              height: 5em;
              text-align: center;
              display: flex;
              justify-content: center;
              align-items: center;
              font-size: .9em;

              .tag-content {
                font-size: .867em;
                color: var(--main-award-badge-text-color);
                text-transform: capitalize;
              }
            }
          }
        }

        .abstract {
          font-size: 1.1em;
        }

       ::v-deep .add-to-cart-container {
          width: 100%;

          @include media-breakpoint-up(md) {
            max-width: 22em;
          }

         ::v-deep .add-to-cart {
            margin: 2em 0;

            .form-control {
              background: var(--main-secondary);
              color: var(--main-white);
              font-size: 1.4em !important;
              max-width: 5em;
              border-top-left-radius: 1.5em !important;
              border-bottom-left-radius: 1.5em !important;
              padding-left: 2em;
            }

            .btn {

              border-top-right-radius: 1.5em !important;
              border-bottom-right-radius: 1.5em !important;
              font-size: 1.4em !important;

              &:hover {
                background: var(--main-primary) !important;
              }
            }
          }
        }
      }
    }

  }

  .wine-details-wrapper {
    padding: 3em 0em;
    background: var(--main-product-detail-background);
    color: var(--main-text-color);

   ::v-deep .faq-list {
      .faq-category {
        .faqs .faq {
          border-color: var(--main-product-detail-border-color);
        }
      }
    }

    .wine-details-content {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      justify-content: space-between;

      .tasting-desc {
        width: 100%;

        @include media-breakpoint-up(md) {
          min-width: 60%;
          max-width: 60%;
        }
      }

      .wine-details {
        width: 100%;

        @include media-breakpoint-up(md) {
          width: 100%;
          max-width: calc(40% - 2em);
        }
      }

      .wine-details-element {
        &:only-child {
          width: 100%;
          max-width: 100%;
        }
      }

      .tasting-desc {
        font-size: 1em;
        position: relative;

        @include media-breakpoint-down(md) {
          margin-bottom: 1.5em;
        }

      }
    }
  }

  .shipping-information-wrapper {
    .shipping-information {
      display: flex;
      flex-wrap: wrap;
      align-items: center;

      .shipping-content {
        flex: 3;
      }
    }
  }
}

</style>
